import iconUrl from '@/assets/brand/zafroi_logo.png';
import { cn } from '@/lib/utils';

// Cropped wordmark (1230×410, 3:1) lives in public/ — same asset the auth
// page and marketing site use, so the brand stays consistent. The
// src/assets/brand/zafroi_name.png file is the uncropped 1536×1024 original
// with transparent padding; do NOT import it (renders tiny at small heights).
// Base-aware so it resolves under the workspace path build (app.zafroi.com/operations/brand/…)
// as well as the subdomain root (ADR-023). import.meta.env.BASE_URL ends with '/'.
const NAME_URL = `${import.meta.env.BASE_URL}brand/zafroi-name.png`;

interface Props {
  variant?: 'mark' | 'lockup';
  className?: string;
  /** Icon size for the mark (used in collapsed sidebar where the wordmark
   *  doesn't fit at 68px width). */
  iconClassName?: string;
  /** Wordmark image class (height) for the lockup. */
  nameClassName?: string;
  /** Tagline shown beneath the wordmark in the lockup. Defaults to "ERP". */
  tagline?: string;
  /** Tagline text class for the lockup. */
  taglineClassName?: string;
  /** Adds a subtle saffron drop shadow — useful on dark surfaces. */
  glow?: boolean;
}

export function Logo({
  variant = 'lockup',
  className,
  iconClassName,
  nameClassName,
  tagline = 'ERP',
  taglineClassName,
  glow,
}: Props) {
  if (variant === 'mark') {
    return (
      <img
        src={iconUrl}
        alt="Zafroi"
        className={cn(
          'h-9 w-9 select-none',
          glow && 'drop-shadow-[0_2px_8px_rgba(255,122,26,0.35)]',
          className,
          iconClassName,
        )}
        draggable={false}
      />
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-2 leading-none', className)}>
      <img
        src={NAME_URL}
        alt="Zafroi"
        className={cn(
          'h-7 w-auto select-none',
          glow && 'drop-shadow-[0_2px_10px_rgba(255,122,26,0.40)]',
          nameClassName,
        )}
        draggable={false}
      />
      {tagline && (
        <>
          {/* Divider + tagline both render in saffron (theme --primary)
              so the brand color carries through the lockup —
              "Zafroi | ERP" reads as one unified mark. */}
          <span aria-hidden="true" className="h-5 w-px bg-primary/70" />
          <span
            className={cn(
              'text-[11px] font-bold uppercase tracking-[0.22em] text-primary',
              taglineClassName,
            )}
          >
            {tagline}
          </span>
        </>
      )}
    </span>
  );
}
